import type { UUID } from '@cmg/common';
import { Alert, CircularProgress, LoadingButton, SnackbarManager, Stack } from '@cmg/design-system';
import { useIntl } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import { Fragment, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useRoutes } from '../../../../../common/hooks/useRoutes/useRoutes';
import { IdentityPageContent } from '../../../../../design-system/IdentityPageContent';
import { RolodexRecordStatus } from '../../../../../graphql';
import { LocalHighlights } from '../components/LocalHighlights';
import { ContactInformationSection } from './components/ContactInformationSection';
import { refetchFirmInvestorCoverageGridReactiveVar } from './components/InvestorCoverageGrid/InvestorCoverageGrid.model';
import { InvestorCoverageSection } from './components/InvestorCoverageSection';
import UnlinkedUserAccountError from './components/UnlinkedUserAccountError';
import { UserAccountSection } from './components/UserAccountSection';
import { UserDetailsSection } from './components/UserDetailsSection';
import { useSalesCoverageUnderwriterEmployeeDetailQuery } from './hooks/useSalesCoverageUnderwriterEmployeeDetailQuery';
import { useUpdateUnderwriterEmployeeStatus } from './hooks/useUpdateUnderwriterEmployeeStatus';

export function CRMSalesCoverageEmployeeDetailRoute() {
  const { crmIntegrationId, employeeKey = '' } = useParams<{
    crmIntegrationId?: UUID;
    employeeKey: string;
  }>();

  const {
    employee,
    loading,
    error: listQueryError,
  } = useSalesCoverageUnderwriterEmployeeDetailQuery(employeeKey, crmIntegrationId);

  const { updateUnderwriterEmployeeStatus, loading: statusUpdateLoading } =
    useUpdateUnderwriterEmployeeStatus(crmIntegrationId);

  const updateEmployeeStatus = useCallback(
    async (status: RolodexRecordStatus) => {
      if (!employeeKey) {
        return;
      }

      try {
        await updateUnderwriterEmployeeStatus({
          employeeKeyOrId: employeeKey,
          input: {
            status,
          },
        });
        SnackbarManager.success('Employee status updated');
      } catch {
        SnackbarManager.error('Failed to update employee status');
      }
    },
    [employeeKey, updateUnderwriterEmployeeStatus]
  );

  const handleDisableEmployeeStatus = useCallback(() => {
    void updateEmployeeStatus(RolodexRecordStatus.Deleted);
    refetchFirmInvestorCoverageGridReactiveVar(true);
  }, [updateEmployeeStatus]);

  const handleEnableEmployeeStatus = useCallback(() => {
    void updateEmployeeStatus(RolodexRecordStatus.Effective);
    refetchFirmInvestorCoverageGridReactiveVar(true);
  }, [updateEmployeeStatus]);

  const { crmSalesCoverage } = useRoutes(crmIntegrationId);

  const { formatDate } = useIntl();

  const breadcrumbs = useMemo(
    () => [
      { label: 'Sales Coverage', href: crmSalesCoverage.getUrlPath({ crmIntegrationId }) },
      { label: defaultTo(employee?.name ?? employee?.employeeKey, '') },
    ],
    [crmSalesCoverage, crmIntegrationId, employee?.name, employee?.employeeKey]
  );

  const metaInfoLabels = useMemo(
    () => [
      {
        label: 'Created',
        value: `${formatDate(employee?.createdOn, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })} by ${employee?.createdByUserName ?? 'Unknown'}`,
      },
      {
        label: 'Modified',
        value: `${formatDate(employee?.modifiedOn, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })} by ${employee?.modifiedByUserName ?? 'Unknown'}`,
      },
    ],
    [
      formatDate,
      employee?.createdByUserName,
      employee?.createdOn,
      employee?.modifiedOn,
      employee?.modifiedByUserName,
    ]
  );

  const actions = useMemo(() => {
    if (employee?.status === RolodexRecordStatus.Effective) {
      return [
        <LoadingButton
          key="disable-employee-button"
          variant="contained"
          color="error"
          onClick={handleDisableEmployeeStatus}
          loading={statusUpdateLoading}
        >
          Disable Employee
        </LoadingButton>,
      ];
    }

    return [
      <LoadingButton
        key="enable-employee-button"
        variant="outlined"
        onClick={handleEnableEmployeeStatus}
        loading={statusUpdateLoading}
      >
        Enable Employee
      </LoadingButton>,
    ];
  }, [
    employee?.status,
    handleDisableEmployeeStatus,
    handleEnableEmployeeStatus,
    statusUpdateLoading,
  ]);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    );
  }

  if (listQueryError) {
    return <Alert severity="error">Unable to load employee</Alert>;
  }

  if (!employee) {
    return <Alert severity="error">Employee not found</Alert>;
  }

  return (
    <IdentityPageContent
      slotProps={{ grid: { flex: 1 } }}
      localHighlights={
        <LocalHighlights
          title={employee.name ?? employee.employeeKey}
          status={employee.status}
          breadcrumbs={breadcrumbs}
          metaInfoLabels={metaInfoLabels}
          actions={actions}
          errors={!employee.userAccount && <UnlinkedUserAccountError />}
        />
      }
      gridContent={
        <Fragment>
          <UserDetailsSection tenantId={crmIntegrationId} employee={employee} />
          <ContactInformationSection tenantId={crmIntegrationId} employee={employee} />
          <UserAccountSection employee={employee} />
          <InvestorCoverageSection
            tenantId={crmIntegrationId}
            employeeId={employee.id}
            employeeStatus={employee.status}
          />
        </Fragment>
      }
    />
  );
}

export default CRMSalesCoverageEmployeeDetailRoute;
